import React, { useEffect, useRef, useState } from "react";
import { apiRequest } from "../api/client.js";
import { Edit, TrendingUp } from "../shared/icons.jsx";
import { Avatar, Button, Field, MonoLabel, cx, initialsOf } from "../shared/ui.jsx";
import { mmss, toDate } from "../shared/dates.js";

const points = (value) => value === null || value === undefined ? "-" : Number(value).toFixed(1);

const fitNum = (n) => Number(n || 0).toLocaleString();

// Daily steps / miles / cardio minutes pulled from a connected wearable.
function FitnessPanel({ fitness, busy, onConnect, onSync, onDisconnect }) {
  if (!fitness?.configured) return null;
  const conn = (fitness.connections || []).find((c) => c.provider === "google_health");
  const w = fitness.summary?.windows || { "7": {}, "30": {}, all: {} };
  const Row = ({ label, field }) => (
    <tr>
      <td className="mono text-[10px] uppercase tracking-[0.1em] text-muted py-1">{label}</td>
      <td className="text-right text-ink tabular-nums">{fitNum(w["7"]?.[field])}</td>
      <td className="text-right text-ink tabular-nums">{fitNum(w["30"]?.[field])}</td>
      <td className="text-right text-ink tabular-nums">{fitNum(w.all?.[field])}</td>
    </tr>
  );
  return (
    <div className="bg-white px-4 pt-4 pb-4 mb-5">
      <div className="flex justify-between items-center mb-3">
        <MonoLabel className="text-[9px]">Activity</MonoLabel>
        {conn ? (
          <Button size="sm" variant="outline" onClick={onSync} disabled={busy}>{busy ? "Syncing…" : "Sync"}</Button>
        ) : (
          <Button size="sm" onClick={() => onConnect("google_health")}>Connect Google Health</Button>
        )}
      </div>
      {conn ? (
        <>
          <table className="w-full">
            <thead>
              <tr className="mono text-[8px] uppercase tracking-[0.1em] text-muted">
                <th></th>
                <th className="text-right font-normal">7d</th>
                <th className="text-right font-normal">30d</th>
                <th className="text-right font-normal">All</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              <Row label="Steps" field="steps" />
              <Row label="Miles" field="miles" />
              <Row label="Cardio min" field="cardio" />
            </tbody>
          </table>
          <div className="flex justify-between items-center mt-3">
            <span className="mono text-[9px] text-muted">
              {conn.status === "error" ? "Sync error — reconnect" : conn.last_synced_at ? `Synced ${conn.last_synced_at}` : "Not synced yet"}
            </span>
            <button type="button" onClick={() => onDisconnect("google_health")} className="mono text-[10px] text-fail bg-transparent border-0 cursor-pointer p-0 underline">Disconnect</button>
          </div>
        </>
      ) : (
        <div className="mono text-[10px] text-muted">Connect to pull your daily steps, miles, and cardio minutes.</div>
      )}
    </div>
  );
}

const STRENGTH_LABELS = { pushup: "Push-ups", hr_pushup: "Hand-release" };
const CORE_LABELS = { situp: "Sit-ups", clr_crunch: "CLR crunch", plank: "Plank" };
const CARDIO_LABELS = { run: "2-mile", hamr: "20m HAMR" };

const assessmentDate = (assessment) =>
  toDate(assessment.starts_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });

const cardioRaw = (assessment) =>
  assessment.manual_cardio_value ||
  (assessment.stopwatch_dnf === 1
    ? "DNF"
    : assessment.stopwatch_cardio_seconds
      ? mmss(Number(assessment.stopwatch_cardio_seconds))
      : "-");

const statusChip = (assessment) => {
  if (assessment.status === "voided") return ["Voided", "bg-[rgba(176,86,58,0.14)] text-fail"];
  if (assessment.passed === 1) return ["Pass", "bg-[rgba(95,107,57,0.14)] text-pass-deep"];
  if (assessment.passed === 0) return ["Does not meet", "bg-[rgba(176,86,58,0.14)] text-fail"];
  return [assessment.status, "bg-line-soft text-muted"];
};

function ComponentBar({ label, note, value, max }) {
  const numeric = value === null || value === undefined ? null : Number(value);
  return (
    <div>
      <div className="flex justify-between items-baseline mb-1.5">
        <span className="mono text-[10px] font-semibold uppercase tracking-[0.1em] text-ink">{label}</span>
        <span className="mono text-[10px] text-muted">{note}</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1 h-1.5 bg-[#ecebe8]">
          <div
            className="h-full bg-signal"
            style={{ width: numeric === null ? 0 : `${Math.max(0, Math.min(100, (numeric / max) * 100))}%` }}
          />
        </div>
        <span className="display text-xl leading-[0.8] w-16 text-right text-ink">
          {points(value)}<span className="mono text-[9px] text-muted">/{max}</span>
        </span>
      </div>
    </div>
  );
}

const ATTENDANCE_COLORS = {
  present: "oklch(0.5 0.13 150)",
  excused: "oklch(0.6 0.13 80)",
  absent: "#c0563a"
};

export default function MemberHistory({ principal, changed }) {
  const [history, setHistory] = useState(null);
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({
    displayName: principal.name || "",
    rank: principal.rank || ""
  });
  const [error, setError] = useState("");
  const [fitnessBusy, setFitnessBusy] = useState(false);
  const autoSynced = useRef(false);

  const load = async () => {
    try {
      setHistory(await apiRequest("member_history"));
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => { load(); }, []);

  const connectFitness = async (provider = "google_health") => {
    try {
      const res = await apiRequest("connect_fitness", { method: "POST", body: { provider } });
      window.location.href = res.authorizeUrl;
    } catch (err) {
      setError(err.message);
    }
  };

  const syncFitness = async () => {
    setFitnessBusy(true);
    try {
      await apiRequest("sync_fitness", { method: "POST", body: {} });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setFitnessBusy(false);
    }
  };

  const disconnectFitness = async (provider) => {
    if (!window.confirm("Disconnect this account? Synced history is kept.")) return;
    try {
      await apiRequest("disconnect_fitness", { method: "POST", body: { provider } });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  // Sync once on open if a connection is stale (older than ~6h or never synced).
  useEffect(() => {
    if (!history || autoSynced.current) return;
    const stale = (history.fitness?.connections || []).some(
      (c) => c.status === "active" &&
        (!c.last_synced_at || Date.now() - new Date(c.last_synced_at.replace(" ", "T")).getTime() > 6 * 3600 * 1000)
    );
    if (stale) { autoSynced.current = true; syncFitness(); }
  }, [history]);

  const saveProfile = async (event) => {
    event.preventDefault();
    try {
      await apiRequest("update_member_profile", { method: "POST", body: profile });
      setEditing(false);
      await changed();
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const scored = (history?.assessments || []).filter(
    (assessment) => assessment.status !== "voided" && assessment.total_points !== null && assessment.total_points !== undefined
  );
  const latest = scored[0];
  const previous = scored[1];
  const delta = latest && previous ? Number(latest.total_points) - Number(previous.total_points) : null;

  return (
    <div>
      {/* profile header */}
      <div className="bg-ink -mx-4 -mt-5 px-5 pt-4 pb-4 mb-5">
        <div className="flex items-center gap-3.5">
          <Avatar initials={initialsOf(principal.name)} size={52} tone="signal" />
          <div className="flex-1 min-w-0">
            <h2 className="display text-3xl leading-[0.9] text-white m-0 truncate">{principal.name}</h2>
            <div className="mono text-[9px] uppercase tracking-[0.16em] text-stone-500 mt-1">
              {principal.flightName} · {principal.squadronName}
            </div>
          </div>
          <button
            onClick={() => setEditing((value) => !value)}
            aria-label="Edit profile"
            className="border border-ink-line text-muted hover:text-white bg-transparent cursor-pointer p-2 flex"
          >
            <Edit size={17} />
          </button>
        </div>
        {editing && (
          <form className="mt-4 grid grid-cols-[1fr_120px] gap-2.5" onSubmit={saveProfile}>
            <Field dark placeholder="Display name" value={profile.displayName} onChange={(e) => setProfile({ ...profile, displayName: e.target.value })} />
            <Field dark placeholder="Rank" value={profile.rank} onChange={(e) => setProfile({ ...profile, rank: e.target.value })} />
            <Button className="col-span-2">Update Profile</Button>
          </form>
        )}
      </div>

      {error && (
        <div className="border border-[#c98b8b] bg-[rgba(176,86,58,0.1)] text-red-800 p-3 text-sm mb-4">{error}</div>
      )}

      {!history ? (
        <div className="text-muted mono text-xs uppercase tracking-[0.12em]">Loading history...</div>
      ) : (
        <>
          <FitnessPanel
            fitness={history.fitness}
            busy={fitnessBusy}
            onConnect={connectFitness}
            onSync={syncFitness}
            onDisconnect={disconnectFitness}
          />

          {/* latest result hero */}
          {latest && (
            <div className="bg-white px-4 pt-4 pb-4 mb-5">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <MonoLabel className="text-[9px]">Latest composite</MonoLabel>
                  <div className="flex items-end gap-3 mt-1">
                    <span className="display text-[64px] leading-[0.74] text-ink">{points(latest.total_points)}</span>
                    {delta !== null && (
                      <span className={cx(
                        "flex items-center gap-1 mono text-xs font-semibold pb-2",
                        delta >= 0 ? "text-pass-deep" : "text-fail"
                      )}>
                        <TrendingUp size={13} className={delta >= 0 ? "" : "rotate-180"} />
                        {delta >= 0 ? "+" : ""}{delta.toFixed(1)}
                      </span>
                    )}
                  </div>
                </div>
                <span className={cx("mono text-[10px] font-semibold uppercase tracking-[0.12em] px-2.5 py-1.5", statusChip(latest)[1])}>
                  {statusChip(latest)[0]}
                </span>
              </div>
              <div className="mono text-[10px] text-muted mb-4">{latest.event_title} · {assessmentDate(latest)}</div>
              <div className="flex flex-col gap-3">
                <ComponentBar label="Cardio" note={`${CARDIO_LABELS[latest.cardio_type] || "Cardio"} · ${cardioRaw(latest)}`} value={latest.cardio_points} max={50} />
                <ComponentBar label="Strength" note={`${STRENGTH_LABELS[latest.strength_type] || "Strength"} · ${latest.strength_value ?? "-"}`} value={latest.strength_points} max={15} />
                <ComponentBar label="Core" note={`${CORE_LABELS[latest.core_type] || "Core"} · ${latest.core_value || "-"}`} value={latest.core_points} max={15} />
                <ComponentBar label="BCA" note={`WHtR · ${latest.bca_whtr || "-"}`} value={latest.bca_points} max={20} />
              </div>
            </div>
          )}

          {/* assessment history */}
          <MonoLabel className="text-[10px] font-semibold text-ink block mb-3">Assessment history</MonoLabel>
          {history.assessments.length === 0 ? (
            <div className="border border-dashed border-line p-8 text-center text-muted mono text-xs uppercase tracking-[0.12em] mb-6">
              No mock assessments recorded.
            </div>
          ) : (
            <div className="flex flex-col gap-3 mb-6">
              {history.assessments.map((assessment) => {
                const [chipLabel, chipClass] = statusChip(assessment);
                return (
                  <article key={assessment.id} className="bg-white px-4 py-3.5">
                    <div className="flex items-center gap-3.5">
                      <div className="flex-1 min-w-0">
                        <div className="text-[15px] font-semibold text-ink truncate">{assessment.event_title}</div>
                        <div className="mono text-[10px] text-muted mt-0.5">{assessmentDate(assessment)}</div>
                      </div>
                      <span className={cx("mono text-[9px] font-semibold uppercase tracking-[0.1em] px-2 py-1 flex-shrink-0", chipClass)}>
                        {chipLabel}
                      </span>
                      <div className="text-right w-16 flex-shrink-0">
                        <div className="display text-[28px] leading-[0.78] text-ink">{points(assessment.total_points)}</div>
                        <div className="mono text-[8px] uppercase tracking-[0.1em] text-muted mt-0.5">Composite</div>
                      </div>
                    </div>
                    <div className="mono text-[10px] text-muted mt-2.5">
                      BCA {points(assessment.bca_points)} · Strength {points(assessment.strength_points)} · Core {points(assessment.core_points)} · Cardio {points(assessment.cardio_points)}
                      <span className="text-[#c4c0b9]"> | </span>
                      WHtR {assessment.bca_whtr || "-"} · {STRENGTH_LABELS[assessment.strength_type] || "strength"} {assessment.strength_value ?? "-"} · {CORE_LABELS[assessment.core_type] || "core"} {assessment.core_value || "-"} · {CARDIO_LABELS[assessment.cardio_type] || "cardio"} {cardioRaw(assessment)}
                    </div>
                    {assessment.amendments?.map((item, index) => (
                      <div key={`${item.created_at}-${index}`} className="border-t border-line-soft mt-2 pt-2 text-xs text-stone-600">
                        Amended: {item.reason} ({item.applied_as_current === 1 ? "current" : "retained in history"})
                      </div>
                    ))}
                  </article>
                );
              })}
            </div>
          )}

          {/* attendance */}
          <MonoLabel className="text-[10px] font-semibold text-ink block mb-3">Recent attendance</MonoLabel>
          <div className="bg-white">
            {history.attendance.map((item) => {
              const status = item.attendance_status || null;
              const color = status ? ATTENDANCE_COLORS[status] || "#9ca0a6" : "#9ca0a6";
              return (
                <div key={item.registration_id} className="px-4 py-3 border-b border-line-soft last:border-b-0 flex justify-between items-center gap-3">
                  <span className="text-sm font-medium text-ink min-w-0 truncate">
                    {item.title}
                    <span className="mono text-[10px] text-muted ml-2">{item.owner_flight_name}</span>
                  </span>
                  <span className="flex items-center gap-2 mono text-[10px] font-semibold uppercase tracking-[0.08em] flex-shrink-0" style={{ color }}>
                    <span className="w-[7px] h-[7px] rounded-full inline-block" style={{ background: color }} />
                    {status || "Registered"}
                  </span>
                </div>
              );
            })}
            {history.attendance.length === 0 && (
              <div className="px-4 py-6 text-muted text-sm">No attendance recorded.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
